import { useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';

import { useHovering } from '../../hooks/useHovering';
import type { Account } from '../../types/resources';
import { AvatarConversation } from './avatar_conversation';
import { me } from '../initial_state';
import { autoPlayGif } from '../initial_state';

interface Props {
  account: Account | undefined; // FIXME: remove `undefined` once we know for sure its always there
  size: number;
  style?: React.CSSProperties;
  inline?: boolean;
  animate?: boolean;
}

export const Avatar: React.FC<Props> = ({
  account,
  animate = autoPlayGif,
  size = 20,
  inline = false,
  style: styleFromParent,
}) => {
  const { hovering, handleMouseEnter, handleMouseLeave } = useHovering(animate);

  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const element = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const accountId = account?.get('id');
  const eligible = Boolean(me && accountId && accountId !== me);
  const tooltipId = useId();
  const cancel = () => { if (timer.current) clearTimeout(timer.current); };
  const open = () => {
    cancel();
    if (eligible) timer.current = setTimeout(() => {
      if (element.current) setAnchor(element.current.getBoundingClientRect());
    }, 400);
  };
  const close = () => { cancel(); timer.current = setTimeout(() => setAnchor(null), 150); };
  useEffect(() => {
    setAnchor(null);
    const dismiss = (event: Event) => {
      if (event.target instanceof Element && event.target.closest('.avatar-conversation')) return;
      if (timer.current) clearTimeout(timer.current);
      setAnchor(null);
    };
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => { if (timer.current) clearTimeout(timer.current); window.removeEventListener('scroll', dismiss, true); window.removeEventListener('resize', dismiss); };
  }, [accountId]);

  const style = {
    ...styleFromParent,
    width: `${size}px`,
    height: `${size}px`,
  };

  const src =
    hovering || animate
      ? account?.get('avatar')
      : account?.get('avatar_static');

  return (
    <div
      className={classNames('account__avatar', {
        'account__avatar-inline': inline,
      })}
      ref={element}
      tabIndex={eligible ? 0 : undefined}
      aria-describedby={anchor ? tooltipId : undefined}
      onFocus={open}
      onBlur={close}
      onKeyDown={event => { if (event.key === 'Escape') { cancel(); setAnchor(null); } }}
      onMouseEnter={() => { handleMouseEnter(); open(); }}
      onMouseLeave={() => { handleMouseLeave(); close(); }}
      style={style}
    >
      {src && <img src={src} alt={account?.get('acct')} />}
      {anchor && accountId && <AvatarConversation accountId={accountId} anchor={anchor} id={tooltipId} onEnter={cancel} onLeave={close} />}
    </div>
  );
};
